import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { BruloHeader } from "@/components/brulo-header";
import { BruloMap, escapeHtml, safeHttpUrl, type MapPoint } from "@/components/brulo-map";
import { supabase } from "@/integrations/supabase/client";
import { buildCafeAddress } from "@/lib/mexican-states";
import { useAuth } from "@/hooks/use-auth";
import { useUserCity } from "@/hooks/use-user-city";
import { Calendar, MapPin, Users, Coffee, Check, Footprints, Bike, Mountain, PersonStanding, Clock } from "lucide-react";
import { toast } from "sonner";
import { EventStatusBadge, type EstadoConfirmacion } from "@/components/event-status-badge";
import { ShareEventButton } from "@/components/share-event-button";
import { JoinConsentDialog } from "@/components/join-consent-dialog";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Brulo — run · ride · gather" },
      {
        name: "description",
        content:
          "Brulo conecta run clubs y cycling clubs con cafeterías y restaurantes. Encuentra eventos, descubre rutas y disfruta del café después.",
      },
      { property: "og:title", content: "Brulo — run · ride · gather" },
      {
        property: "og:description",
        content: "Conecta clubs de running y ciclismo con cafeterías locales.",
      },
    ],
  }),
  component: Home,
});

type Role = "runner" | "organizer" | "cafe";

type EventRow = {
  id: string;
  name: string;
  event_date: string;
  event_time: string;
  meeting_point: string;
  distance_km: number | null;
  organizer_id: string;
  cafe_id: string | null;
  organizador_tipo: "club" | "cafeteria";
  tipo_actividad?: "running" | "ciclismo" | "trail" | "caminata" | "ambos" | "social" | null;
  estado_confirmacion: EstadoConfirmacion;
  cancelado?: boolean;
  created_at: string;
  clubs: { name: string; type: string } | null;
};
type InscritosMap = Record<string, { count: number; names: string[] }>;

async function fetchInscritosMap(eventIds: string[]): Promise<InscritosMap> {
  if (eventIds.length === 0) return {};
  const { data: ins } = await supabase
    .from("inscripciones")
    .select("evento_id,usuario_id,fecha_inscripcion")
    .in("evento_id", eventIds)
    .order("fecha_inscripcion");
  const list = ins ?? [];
  const userIds = Array.from(new Set(list.map((i) => i.usuario_id)));
  const { data: profs } = userIds.length
    ? await supabase.from("profiles").select("id,full_name").in("id", userIds)
    : { data: [] as { id: string; full_name: string | null }[] };
  const nameMap = new Map((profs ?? []).map((p) => [p.id, p.full_name ?? ""]));
  const result: InscritosMap = {};
  list.forEach((i) => {
    const slot = (result[i.evento_id] ??= { count: 0, names: [] });
    slot.count += 1;
    if (slot.names.length < 3) slot.names.push(nameMap.get(i.usuario_id) ?? "");
  });
  return result;
}

function Home() {
  const { user, loading } = useAuth();
  const [role, setRole] = useState<Role | null>(null);
  const [displayName, setDisplayName] = useState<string>("");
  const [roleLoaded, setRoleLoaded] = useState(false);

  useEffect(() => {
    if (!user) {
      setRoleLoaded(true);
      return;
    }
    (async () => {
      const { data: prof } = await supabase
        .from("profiles")
        .select("role,full_name")
        .eq("id", user.id)
        .maybeSingle();
      const r = (prof?.role as Role) ?? "runner";
      setRole(r);
      // For organizer use club name, for cafe use cafe name
      if (r === "organizer") {
        const { data: club } = await supabase
          .from("clubs")
          .select("name")
          .eq("owner_id", user.id)
          .maybeSingle();
        setDisplayName(club?.name ?? prof?.full_name ?? "");
      } else if (r === "cafe") {
        const { data: cafe } = await supabase
          .from("cafes")
          .select("name")
          .eq("owner_id", user.id)
          .maybeSingle();
        setDisplayName(cafe?.name ?? prof?.full_name ?? "");
      } else {
        setDisplayName(prof?.full_name ?? "");
      }
      setRoleLoaded(true);
    })();
  }, [user]);

  if (loading || !roleLoaded) {
    return (
      <div className="min-h-screen brulo-grain">
        <BruloHeader />
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-20 text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!user) return <Visitor />;

  return (
    <div className="min-h-screen brulo-grain">
      <BruloHeader />
      {role === "runner" && <RunnerHome userId={user.id} name={displayName} />}
      {role === "organizer" && <OrganizerHome userId={user.id} clubName={displayName} />}
      {role === "cafe" && <CafeHome userId={user.id} cafeName={displayName} />}
    </div>
  );
}

/* ====================== VISITOR LANDING ====================== */

function Visitor() {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [stats, setStats] = useState<{ runners: number; clubs: number; cafes: number }>({
    runners: 0,
    clubs: 0,
    cafes: 0,
  });
  const [inscritosMap, setInscritosMap] = useState<InscritosMap>({});

  useEffect(() => {
    (async () => {
      const today = new Date().toISOString().split("T")[0];
      const [evRes, countsRes] = await Promise.all([
        supabase
          .from("events")
          .select("id,name,event_date,event_time,meeting_point,distance_km,organizer_id,cafe_id,organizador_tipo,tipo_actividad,estado_confirmacion,cancelado,created_at,clubs(name,type)")
          .gte("event_date", today)
          .order("event_date", { ascending: true })
          .limit(3),
        supabase.rpc("get_community_counts").maybeSingle(),
      ]);
      const list = ((evRes.data ?? []) as unknown as EventRow[]).filter((e) => e.estado_confirmacion !== "declinado" && !e.cancelado);
      setEvents(list);
      const c = (countsRes.data ?? null) as { runners: number | null; clubs: number | null; cafes: number | null } | null;
      setStats({
        runners: Number(c?.runners ?? 0) || 0,
        clubs: Number(c?.clubs ?? 0) || 0,
        cafes: Number(c?.cafes ?? 0) || 0,
      });
      setInscritosMap(await fetchInscritosMap(list.map((e) => e.id)));
      setLoaded(true);
    })();
  }, []);

  return (
    <div className="min-h-screen brulo-grain">
      <BruloHeader />

      <section className="relative">
        <div className="mx-auto max-w-6xl px-4 md:px-6 pt-16 md:pt-24 pb-20 md:pb-32">
          <p className="text-primary text-xs sm:text-sm tracking-[0.3em] uppercase mb-6">
            community · coffee · kilometers
          </p>
          <h1 className="font-display text-5xl sm:text-7xl md:text-9xl leading-[0.95] text-balance break-words">
            run<span className="text-primary"> · </span>ride
            <span className="text-primary"> · </span>gather
          </h1>
          <p className="mt-8 max-w-2xl font-display text-2xl sm:text-4xl md:text-5xl leading-tight tracking-wide text-foreground">
            No corres solo. <span className="text-primary">Nunca lo hiciste.</span>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {[
              { icon: <Footprints className="w-4 h-4 text-primary" />, value: stats.runners, label: "corredores" },
              { icon: <Users className="w-4 h-4 text-primary" />, value: stats.clubs, label: "clubs" },
              { icon: <Coffee className="w-4 h-4 text-primary" />, value: stats.cafes, label: "locales" },
            ].map((s) => (
              <span
                key={s.label}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-muted-foreground/20 bg-muted-foreground/[0.04] text-sm text-muted-foreground"
              >
                {s.icon}
                <span className="text-foreground font-semibold">{s.value}</span>
                <span>{s.label}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="h-px w-full" style={{ backgroundColor: "#3a3530" }} />

      <section>
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-16 md:py-24 grid md:grid-cols-3 gap-8 md:gap-12">
          {[
            {
              t: "Para corredores",
              d: "Encuentra eventos cerca de ti, únete a clubs y descubre cafeterías con descuento para miembros.",
              icon: <Footprints className="w-10 h-10 text-primary" strokeWidth={1.5} />,
            },
            {
              t: "Para organizadores",
              d: "Crea y gestiona salidas en segundos. Punto de reunión, distancia, ritmo y café incluido.",
              icon: <Users className="w-10 h-10 text-primary" strokeWidth={1.5} />,
            },
            {
              t: "Para locales",
              d: "Cafeterías, restaurantes y cualquier lugar que quiera ser el punto de encuentro de la comunidad fit de su ciudad.",
              icon: <Coffee className="w-10 h-10 text-primary" strokeWidth={1.5} />,
            },
          ].map((p) => (
            <div key={p.t} className="border-l-2 border-primary pl-6">
              <div className="mb-5">{p.icon}</div>
              <h3 className="font-display text-2xl tracking-wider mb-4">{p.t}</h3>
              <p className="text-muted-foreground leading-loose text-lg">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-border/60">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-16 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10 md:mb-12">
            <div>
              <p className="text-primary text-xs tracking-[0.3em] uppercase mb-2">eventos próximos</p>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl">Esta semana</h2>
            </div>
            <Link to="/auth" className="text-sm text-muted-foreground hover:text-primary transition">
              Ver todos →
            </Link>
          </div>

          {loaded && events.length === 0 ? (
            <div className="bg-card/50 border border-dashed border-border rounded-lg p-12 text-center">
              <p className="text-muted-foreground mb-4">
                No hay eventos esta semana — ¿tienes un club? Crea el primero.
              </p>
              <Link
                to="/auth"
                className="inline-flex items-center justify-center min-h-11 px-6 py-2.5 rounded-md bg-primary text-primary-foreground text-sm sm:text-base font-semibold tracking-wide hover:opacity-90 transition whitespace-normal break-words text-center leading-tight"
              >
                Crear mi club
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 w-full">
              {events.map((ev) => (
                <PublicEventCard key={ev.id} ev={ev} inscritos={inscritosMap[ev.id]} />
              ))}
            </div>
          )}
        </div>
      </section>

      <VisitorMapSection />



      <footer className="border-t border-border/60 py-10">
        <div className="mx-auto max-w-6xl px-4 md:px-6 text-sm text-muted-foreground text-center tracking-wide">
          © 2026 Brulo · run · ride · gather
        </div>
      </footer>
    </div>
  );
}

/* ====================== RUNNER HOME ====================== */

function RunnerHome({ userId, name }: { userId: string; name: string }) {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [mine, setMine] = useState<Set<string>>(new Set());
  const [inscritosMap, setInscritosMap] = useState<InscritosMap>({});
  const [clubs, setClubs] = useState<{ id: string; name: string; type: string; city: string | null }[]>([]);
  const [cafes, setCafes] = useState<
    {
      id: string;
      name: string;
      location: string;
      city: string | null;
      hours: string | null;
      opens_at: string | null;
      closes_at: string | null;
      member_discount: string | null;
      google_maps_url: string | null;
    }[]
  >([]);

  const [pendingJoin, setPendingJoin] = useState<string | null>(null);

  const load = async () => {
    const today = new Date().toISOString().split("T")[0];
    const [ev, ins, cl, cf] = await Promise.all([
      supabase
        .from("events")
        .select("id,name,event_date,event_time,meeting_point,distance_km,organizer_id,cafe_id,organizador_tipo,tipo_actividad,estado_confirmacion,cancelado,created_at,clubs(name,type)")
        .gte("event_date", today)
        .order("event_date"),
      supabase.from("inscripciones").select("evento_id").eq("usuario_id", userId),
      supabase.from("clubs").select("id,name,type,city").limit(6),
      supabase
        .from("cafes")
        .select("id,name,location,city,hours,opens_at,closes_at,member_discount,google_maps_url")
        .limit(9),
    ]);
    const list = ((ev.data ?? []) as unknown as EventRow[]).filter((e) => e.estado_confirmacion !== "declinado" && !e.cancelado);
    setEvents(list);
    setMine(new Set((ins.data ?? []).map((i: { evento_id: string }) => i.evento_id)));
    setClubs(cl.data ?? []);
    setCafes(cf.data ?? []);
    setInscritosMap(await fetchInscritosMap(list.map((e) => e.id)));
  };

  useEffect(() => {
    load();
  }, [userId]);

  const afterJoin = async (eventoId: string) => {
    setMine((s) => new Set(s).add(eventoId));
    setInscritosMap(await fetchInscritosMap(events.map((e) => e.id)));
  };


  const myEvents = events.filter((e) => mine.has(e.id));

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 space-y-16">
      <Greeting eyebrow="bienvenido" title={name ? `Bienvenido, ${name.split(" ")[0]}` : "Bienvenido"} />

      <Section title="Tus próximas salidas" icon={<Calendar className="w-6 h-6 text-primary" />}>
        {myEvents.length === 0 ? (
          <Empty text="Aún no te has unido a ningún evento." />
        ) : (
          <Grid>
            {myEvents.map((ev) => (
              <EventCard key={ev.id} ev={ev} inscritos={inscritosMap[ev.id]} />
            ))}
          </Grid>
        )}
      </Section>

      <Section title="Eventos esta semana" icon={<Calendar className="w-6 h-6 text-primary" />}>
        {events.length === 0 ? (
          <Empty text="No hay eventos próximos." />
        ) : (
          <Grid>
            {events.map((ev) => (
              <EventCard
                key={ev.id}
                ev={ev}
                inscritos={inscritosMap[ev.id]}
                action={
                  mine.has(ev.id) ? (
                    <button
                      disabled
                      className="px-3 py-2 min-h-10 rounded-md bg-primary/20 text-primary text-sm font-semibold flex items-center gap-1.5"
                    >
                      <Check className="w-3.5 h-3.5" /> Inscrito
                    </button>
                  ) : (
                    <button
                      onClick={() => setPendingJoin(ev.id)}
                      className="px-4 py-2 min-h-10 inline-flex items-center rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
                    >
                      Unirme
                    </button>
                  )
                }
              />
            ))}
          </Grid>
        )}
      </Section>

      <Section title="Clubs cerca de ti" icon={<Users className="w-6 h-6 text-primary" />}>
        {clubs.length === 0 ? (
          <Empty text="Aún no hay clubs registrados." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">

            {clubs.map((c) => (
              <div key={c.id} className="bg-card border border-border rounded-lg p-5">
                <p className="text-xs uppercase tracking-widest text-primary mb-2">
                  {clubTypeLabel(c.type)}
                </p>
                <h3 className="font-display text-xl">{c.name}</h3>
                {c.city && <p className="text-sm text-muted-foreground mt-1">{c.city}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Puntos Brulo cerca de ti" icon={<Coffee className="w-6 h-6 text-primary" />}>
        {cafes.length === 0 ? (
          <Empty text="Aún no hay cafeterías registradas." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">

            {cafes.map((c) => {
              const hours =
                c.opens_at && c.closes_at
                  ? `${c.opens_at.slice(0, 5)} – ${c.closes_at.slice(0, 5)}`
                  : c.hours;
              return (
                <div
                  key={c.id}
                  className="bg-card border border-border rounded-lg p-5 flex flex-col"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-md bg-primary/10 text-primary">
                      <Coffee className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-xl leading-tight">{c.name}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">{c.location}</p>
                    </div>
                  </div>
                  {hours && (
                    <p className="text-sm text-muted-foreground mb-3">
                      <span className="text-foreground">Horario:</span> {hours}
                    </p>
                  )}
                  {c.member_discount && (
                    <span className="inline-block self-start px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold tracking-wide mb-3">
                      {c.member_discount} descuento miembros
                    </span>
                  )}
                  {(() => {
                    const mapsUrl =
                      safeHttpUrl(c.google_maps_url) ||
                      (c.location
                        ? `https://maps.google.com/?q=${encodeURIComponent(
                            [c.location, c.city].filter(Boolean).join(", "),
                          )}`
                        : null);
                    return mapsUrl ? (
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-auto inline-flex items-center gap-1.5 text-sm text-primary hover:underline self-start"
                      >
                        <MapPin className="w-4 h-4" /> Cómo llegar
                      </a>
                    ) : null;
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <JoinConsentDialog
        eventId={pendingJoin}
        eventName={events.find((e) => e.id === pendingJoin)?.name ?? null}
        userId={userId}
        onClose={() => setPendingJoin(null)}
        onJoined={afterJoin}
      />
    </div>

  );
}

function clubTypeLabel(type: string) {
  if (type === "both") return "Runners y Ciclistas";
  if (type === "ride") return "Ciclistas";
  return "Runners";
}

/* ====================== VISITOR MAP SECTION ====================== */

type VisitorCafe = {
  id: string;
  name: string;
  location: string;
  calle: string | null;
  numero_exterior: string | null;
  colonia: string | null;
  codigo_postal: string | null;
  city: string | null;
  estado: string | null;
  member_discount: string | null;
  google_maps_url: string | null;
  latitude: number | null;
  longitude: number | null;
};

function VisitorMapSection() {
  const [cafes, setCafes] = useState<VisitorCafe[]>([]);
  const userCity = useUserCity();
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("cafes")
        .select(
          "id,name,location,calle,numero_exterior,colonia,codigo_postal,city,estado,member_discount,google_maps_url,latitude,longitude",
        );
      setCafes((data ?? []) as unknown as VisitorCafe[]);
    })();
  }, []);

  const points = useMemo<MapPoint[]>(
    () =>
      cafes
        .map((c): MapPoint | null => {
          const address = buildCafeAddress({
            calle: c.calle,
            numero_exterior: c.numero_exterior,
            colonia: c.colonia,
            codigo_postal: c.codigo_postal,
            city: c.city,
            estado: c.estado,
            location: c.location,
          });
          if (!address) return null;
          const dir =
            safeHttpUrl(c.google_maps_url) ??
            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
          const html = `<div style="font-family:system-ui;max-width:220px;color:#111"><div style="font-weight:700;font-size:14px;margin-bottom:4px">${escapeHtml(c.name)}</div><div style="color:#555;font-size:12px;margin-bottom:6px">${escapeHtml(c.location || address)}</div>${c.member_discount ? `<div style="font-size:12px;color:#D85A30;font-weight:600;margin-bottom:6px">${escapeHtml(c.member_discount)} miembros</div>` : ""}<a href="${escapeHtml(dir)}" target="_blank" rel="noopener" style="display:inline-block;margin-top:4px;padding:5px 10px;background:#D85A30;color:#fff;text-decoration:none;border-radius:6px;font-size:12px;font-weight:600">Cómo llegar</a></div>`;
          return {
            id: `cafe-${c.id}`,
            kind: "cafe",
            title: c.name,
            address,
            html,
            coords:
              c.latitude != null && c.longitude != null
                ? { lat: c.latitude, lng: c.longitude }
                : null,
          };
        })
        .filter((p): p is MapPoint => p !== null),
    [cafes],
  );


  return (
    <section className="border-t border-border/60">
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-16 md:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-primary text-xs tracking-[0.3em] uppercase mb-2">explora</p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl">Puntos Brulo</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl">
              {userCity
                ? `Cafeterías afiliadas en ${userCity} donde se reúne la comunidad.`
                : "Cafeterías afiliadas cerca de ti donde se reúne la comunidad."}
            </p>
          </div>
          <Link to="/mapa" className="text-sm text-muted-foreground hover:text-primary transition">
            Ver mapa completo →
          </Link>
        </div>
        <BruloMap points={points} height={350} />
      </div>
    </section>
  );
}



/* ====================== ORGANIZER HOME ====================== */

function OrganizerHome({ userId, clubName }: { userId: string; clubName: string }) {
  const [mine, setMine] = useState<EventRow[]>([]);
  const [community, setCommunity] = useState<EventRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    (async () => {
      const today = new Date().toISOString().split("T")[0];
      const [own, comm] = await Promise.all([
        supabase
          .from("events")
          .select("id,name,event_date,event_time,meeting_point,distance_km,organizer_id,cafe_id,organizador_tipo,tipo_actividad,estado_confirmacion,cancelado,created_at,clubs(name,type)")
          .eq("organizer_id", userId)
          .gte("event_date", today)
          .order("event_date"),
        supabase
          .from("events")
          .select("id,name,event_date,event_time,meeting_point,distance_km,organizer_id,cafe_id,organizador_tipo,tipo_actividad,estado_confirmacion,cancelado,created_at,clubs(name,type)")
          .neq("organizer_id", userId)
          .gte("event_date", today)
          .order("event_date")
          .limit(6),
      ]);
      const ownList = (own.data ?? []) as unknown as EventRow[];
      setMine(ownList);
      setCommunity(((comm.data ?? []) as unknown as EventRow[]).filter((e) => e.estado_confirmacion !== "declinado" && !e.cancelado));

      if (ownList.length) {
        const { data: ins } = await supabase
          .from("inscripciones")
          .select("evento_id")
          .in("evento_id", ownList.map((e) => e.id));
        const c: Record<string, number> = {};
        (ins ?? []).forEach((i: { evento_id: string }) => {
          c[i.evento_id] = (c[i.evento_id] ?? 0) + 1;
        });
        setCounts(c);
      }
    })();
  }, [userId]);

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 space-y-16">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <Greeting eyebrow="organizador" title={`Hola, ${clubName || "club"}`} />
        <Link
          to="/events/new"
          className="px-6 py-3 rounded-md bg-primary text-primary-foreground font-semibold hover:opacity-90"
        >
          Nueva salida +
        </Link>
      </div>

      <Section title="Tus eventos activos" icon={<Calendar className="w-6 h-6 text-primary" />}>
        {mine.length === 0 ? (
          <Empty text="Aún no has creado eventos." />
        ) : (
          <Grid>
            {mine.map((ev) => (
              <EventCard
                key={ev.id}
                ev={ev}
                footer={
                  <div className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
                    <strong className="text-foreground">{counts[ev.id] ?? 0}</strong> corredores inscritos
                  </div>
                }
              />
            ))}
          </Grid>
        )}
      </Section>

      <Section title="Eventos de la comunidad" icon={<Users className="w-6 h-6 text-primary" />}>
        {community.length === 0 ? (
          <Empty text="No hay otros eventos por ahora." />
        ) : (
          <Grid>
            {community.map((ev) => (
              <EventCard key={ev.id} ev={ev} />
            ))}
          </Grid>
        )}
      </Section>
    </div>
  );
}

/* ====================== CAFE HOME ====================== */

function CafeHome({ userId, cafeName }: { userId: string; cafeName: string }) {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [weekTotal, setWeekTotal] = useState(0);
  const [topClubs, setTopClubs] = useState<{ name: string; visits: number }[]>([]);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [weekEventsCount, setWeekEventsCount] = useState(0);

  useEffect(() => {
    (async () => {
      const { data: cafe } = await supabase
        .from("cafes")
        .select("id,location,hours,description,opens_at,closes_at")
        .eq("owner_id", userId)
        .maybeSingle();
      if (!cafe) {
        setProfileIncomplete(true);
        return;
      }
      const incomplete =
        !cafe.location ||
        !cafe.description ||
        (!cafe.hours && !(cafe.opens_at && cafe.closes_at));
      setProfileIncomplete(incomplete);

      const today = new Date().toISOString().split("T")[0];
      const { data: evs } = await supabase
        .from("events")
        .select("id,name,event_date,event_time,meeting_point,distance_km,organizer_id,cafe_id,organizador_tipo,tipo_actividad,estado_confirmacion,cancelado,created_at,clubs(name,type)")
        .eq("cafe_id", cafe.id)
        .gte("event_date", today)
        .order("event_date");
      const list = (evs ?? []) as unknown as EventRow[];
      setEvents(list);

      const { data: allEvs } = await supabase
        .from("events")
        .select("clubs(name,type)")
        .eq("cafe_id", cafe.id);
      const freq: Record<string, number> = {};
      (allEvs ?? []).forEach((e) => {
        const n = (e as unknown as { clubs: { name: string } | null }).clubs?.name;
        if (n) freq[n] = (freq[n] ?? 0) + 1;
      });
      setTopClubs(
        Object.entries(freq)
          .map(([name, visits]) => ({ name, visits }))
          .sort((a, b) => b.visits - a.visits)
          .slice(0, 6),
      );

      const weekFromNow = new Date();
      weekFromNow.setDate(weekFromNow.getDate() + 7);
      const weekISO = weekFromNow.toISOString().split("T")[0];
      const weekList = list.filter((e) => e.event_date <= weekISO);
      setWeekEventsCount(weekList.length);

      if (list.length) {
        const { data: ins } = await supabase
          .from("inscripciones")
          .select("evento_id")
          .in("evento_id", list.map((e) => e.id));
        const c: Record<string, number> = {};
        (ins ?? []).forEach((i: { evento_id: string }) => {
          c[i.evento_id] = (c[i.evento_id] ?? 0) + 1;
        });
        setCounts(c);

        const weekly = weekList.reduce((acc, e) => acc + (c[e.id] ?? 0), 0);
        setWeekTotal(weekly);
      }
    })();
  }, [userId]);

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 space-y-12">
      <Greeting eyebrow="cafetería brulo" title={cafeName ? `Bienvenido, ${cafeName}` : "Bienvenido"} />

      {profileIncomplete && (
        <Link
          to="/cafe/perfil"
          className="block bg-primary/10 border border-primary/40 rounded-lg p-5 hover:bg-primary/15 transition"
        >
          <p className="text-primary font-medium">
            Completa tu perfil para aparecer en el mapa de Brulo →
          </p>
        </Link>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <StatCard value={weekEventsCount} label="eventos esta semana" />
        <StatCard value={weekTotal} label="deportistas esperados" highlight />
        <StatCard value={topClubs.length} label="clubs que te visitan" />
      </div>

      <Section title="Próximas visitas" icon={<Coffee className="w-6 h-6 text-primary" />}>
        {events.length === 0 ? (
          <Empty text="Aún no hay eventos con tu cafetería como punto de reunión." />
        ) : (
          <Grid>
            {events.map((ev) => (
              <EventCard
                key={ev.id}
                ev={ev}
                footer={
                  <div className="mt-4 pt-4 border-t border-border text-sm">
                    <strong className="text-primary">{counts[ev.id] ?? 0}</strong>{" "}
                    <span className="text-muted-foreground">inscritos</span>
                  </div>
                }
              />
            ))}
          </Grid>
        )}
      </Section>

      <Section title="Clubs frecuentes" icon={<Users className="w-6 h-6 text-primary" />}>
        {topClubs.length === 0 ? (
          <Empty text="Aún no hay clubs frecuentes." />
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {topClubs.map((c) => (
              <div key={c.name} className="bg-card border border-border rounded-lg p-5">
                <h3 className="font-display text-xl">{c.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {c.visits} {c.visits === 1 ? "visita" : "visitas"}
                </p>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function StatCard({
  value,
  label,
  highlight,
}: {
  value: number;
  label: string;
  highlight?: boolean;
}) {
  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <p
        className={`font-display text-5xl ${highlight ? "text-primary" : "text-foreground"}`}
      >
        {value}
      </p>
      <p className="text-sm text-muted-foreground mt-2 uppercase tracking-widest">{label}</p>
    </div>
  );
}

/* ====================== SHARED UI ====================== */

function Greeting({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div>
      <p className="text-primary text-xs tracking-[0.3em] uppercase mb-2">{eyebrow}</p>
      <h1 className="font-display text-4xl sm:text-5xl md:text-6xl">{title}</h1>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="font-display text-3xl mb-6 flex items-center gap-3">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">{children}</div>;
}


function EventCardShell({
  ev,
  action,
  footer,
  inscritos,
  href = "/auth",
  asLink,
}: {
  ev: EventRow;
  action?: React.ReactNode;
  footer?: React.ReactNode;
  inscritos?: { count: number; names: string[] };
  href?: string;
  asLink?: boolean;
}) {
  const ta = ev.tipo_actividad;
  const activity: "running" | "ciclismo" | "trail" | "caminata" | "social" | "ambos" =
    ta === "ciclismo" || ta === "trail" || ta === "caminata" || ta === "running" || ta === "social" || ta === "ambos"
      ? ta
      : (ev.clubs?.type === "ride" || ev.clubs?.type === "ciclismo") ? "ciclismo"
      : ev.clubs?.type === "trail" ? "trail"
      : ev.clubs?.type === "caminata" ? "caminata"
      : ev.clubs?.type === "both" ? "ambos"
      : "running";
  const typeLabel =
    activity === "ciclismo" ? "Ciclismo"
    : activity === "trail" ? "Trail"
    : activity === "caminata" ? "Caminata"
    : activity === "social" ? "Social"
    : activity === "ambos" ? "Mixto"
    : "Running";
  const TypeIcon =
    activity === "ciclismo" ? Bike
    : activity === "trail" ? Mountain
    : activity === "caminata" ? PersonStanding
    : Footprints;
  const isCafeEvent = ev.organizador_tipo === "cafeteria";

  const dateLabel = new Date(`${ev.event_date}T00:00:00`).toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <article className="group relative w-full max-w-full min-w-0 bg-card border border-border rounded-xl p-5 sm:p-6 transition-all hover:border-primary/50 hover:shadow-[0_0_24px_-8px_hsl(var(--primary)/0.4)] overflow-hidden">
      {asLink && (
        <Link to={href} className="absolute inset-0 rounded-xl z-0" aria-label={`Ver ${ev.name}`} />
      )}
      <div className="relative z-10 pointer-events-none">
        <div className="flex items-start justify-between gap-3 mb-5">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-background/60 border border-primary/30 text-primary">
            <TypeIcon className="w-3.5 h-3.5" />
            {typeLabel}
          </span>
          {ev.distance_km != null && (
            <span className="font-display text-3xl leading-none text-primary">
              {ev.distance_km}
              <span className="text-sm ml-0.5">km</span>
            </span>
          )}
        </div>
        {isCafeEvent && (
          <div className="mb-3">
            <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded bg-[oklch(0.7_0.18_25)] text-white">
              Evento del local
            </span>
          </div>
        )}
        {!isCafeEvent && ev.cafe_id && (
          <div className="mb-3">
            <EventStatusBadge estado={ev.estado_confirmacion} createdAt={ev.created_at} />
          </div>
        )}
        {ev.clubs?.name && (
          <p className="text-[10px] uppercase tracking-[0.3em] text-primary font-bold mb-2">
            {ev.clubs.name}
          </p>
        )}
        <h3 className="font-display text-2xl text-foreground leading-tight mb-4 break-words">{ev.name}</h3>
        <div className="h-px bg-border/60 mb-4" />
        <div className="text-sm text-muted-foreground space-y-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary/70 shrink-0" />
            <span className="capitalize">{dateLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary/70 shrink-0" />
            {ev.event_time.slice(0, 5)}
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary/70 shrink-0" />
            <span className="truncate">{ev.meeting_point}</span>
          </div>
        </div>
      </div>

      <div className="relative z-10 mt-5 pt-4 border-t border-border/60 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 pointer-events-none">
          {inscritos && inscritos.count > 0 ? (
            <>
              <div className="flex -space-x-2">
                {inscritos.names.slice(0, 3).map((n, i) => (
                  <div
                    key={i}
                    className="w-7 h-7 rounded-full border-2 border-card bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center"
                  >
                    {(n || "?").slice(0, 1).toUpperCase()}
                  </div>
                ))}
              </div>
              <span className="text-xs text-muted-foreground">
                <span className="text-foreground font-semibold">{inscritos.count}</span>{" "}
                {inscritos.count === 1 ? "va" : "van"}
              </span>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">Sé el primero</span>
          )}
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          <ShareEventButton
            event={{
              id: ev.id,
              name: ev.name,
              event_date: ev.event_date,
              event_time: ev.event_time,
              meeting_point: ev.meeting_point,
              distance_km: ev.distance_km,
              club_name: ev.clubs?.name ?? null,
            }}
          />
          {action}
        </div>
      </div>
      {footer && <div className="relative z-10">{footer}</div>}
    </article>
  );
}

function EventCard(props: {
  ev: EventRow;
  action?: React.ReactNode;
  footer?: React.ReactNode;
  inscritos?: { count: number; names: string[] };
}) {
  return <EventCardShell {...props} />;
}

function PublicEventCard({
  ev,
  inscritos,
}: {
  ev: EventRow;
  inscritos?: { count: number; names: string[] };
}) {
  return (
    <EventCardShell
      ev={ev}
      inscritos={inscritos}
      action={
        <Link
          to="/auth"
          className="px-4 py-2 min-h-10 inline-flex items-center rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition"
        >
          Unirme
        </Link>
      }
    />
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="bg-card/50 border border-dashed border-border rounded-lg p-10 text-center text-muted-foreground">
      {text}
    </div>
  );
}
